// TranslatePanel — fullscreen translation overlay.
//
// Rendered above the reader's iframe area whenever the user has flipped
// translateOn in Settings. Covers the iframe so the visible page reads
// in the target language; the underlying reader is still mounted and
// the side page-flip zones still work, so the user pages through the
// book exactly as normal. The reader's bottom bar stays visible BELOW
// the overlay — that's where Play / Settings / Home live.
//
// Minimal chrome: one ✕ button in the top-left to turn translation off.
// Nothing else. Language picker has been hoisted to Settings. Page
// navigation belongs to the reader's tap zones, not to in-overlay
// buttons. Design brief 2026-06-27.
//
// Per-page translations are cached in two layers:
//   L1 — localStorage, keyed by (bookId, text-hash, target lang).
//        Synchronous, checked first, but evictable / quota-capped.
//   L2 — Dexie `translations` table (same key composed into a string,
//        plus a translator version). Durable across sessions, so
//        reopening a book renders + speaks previously-translated pages
//        instantly with zero network. Dexie hits are backfilled into
//        L1 so the sync pollers (ReaderView's read-aloud loop) see them.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { db } from "@/services/db";
import { remoteUrl } from "@/services/remoteConfig";
import {
  hasPretranslation,
  pageEnglish,
  getBindStatus,
  getLastMiss,
  pretranslationSize,
} from "@/services/pretranslateService";

/** Imperative handle for the overlay's internal pagination. ReaderView's
 *  page turn asks this first: if the translated page still has columns to
 *  show, the turn consumes one of those instead of advancing the book. */
export interface TxNav {
  hasNext: () => boolean;
  hasPrev: () => boolean;
  next: () => void;
  prev: () => void;
  /** Jump to the last column — used when paging BACKWARD into a page, so
   *  the reader lands on its end rather than its start. */
  toEnd: () => void;
}

interface Props {
  bookId: string;
  pageText: string;
  /** Paragraph texts of the visible page, in reading order. When
   *  present the chunker splits on real paragraph boundaries instead
   *  of sentence heuristics. */
  pageParas?: string[];
  pageCfi: string | undefined;
  fontFamily: string;
  fontSize: number;
  lineHeight: number;
  theme: "paper" | "white" | "sepia" | "gray" | "dark" | "eink-light" | "eink-dark";
  /** Target language to translate INTO. */
  lang: string;
  /** Reader margin (px) — used to match the original reading column so
   *  the translated text sits where the user's eye already is. */
  margin: number;
  /** Reserved space at the bottom for the reader's button bar. */
  bottomBarHeight: number;
  /** Look-ahead translation status — driven by ReaderView's pre-fetch
   *  loop. Shown as a thin caption above the button bar so the user
   *  can see how many pages are queued / ready. */
  aheadStatus?: string;
  /** Notifies the parent when a fresh translation is available. Lets
   *  the play loop pick up the new text without re-fetching. */
  onTranslated?: (cfi: string | undefined, translated: string) => void;
  /** Character index into the translated page text currently being
   *  spoken by the read-aloud loop (null when not playing). Drives the
   *  word highlight + auto-scroll while the voice reads the overlay. */
  speechCharIndex?: number | null;
  /** Filled by the panel so ReaderView's page turn can consume the
   *  overlay's own columns before advancing the book. See TX COLUMNS. */
  navRef?: React.MutableRefObject<TxNav | null>;
}

/** Hash a string into a short hex key. djb2-style — fast, stable
 *  across page loads, and good enough for cache lookup. We don't need
 *  cryptographic strength; we just need two pages with the same text
 *  to produce the same key. */
export function textHash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  // Mix top half too so similar prefixes don't collide
  let h2 = 0;
  for (let i = s.length - 1; i >= 0; i--) {
    h2 = ((h2 << 5) + h2 + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36) + "_" + (h2 >>> 0).toString(36) + "_" + s.length;
}

/** Normalize text for hashing so the reader's rendered DOM text matches the
 *  batch-extracted source paragraph. Beyond collapsing whitespace we also:
 *   • NFC-normalize — epub.js can hand back decomposed umlauts/accents
 *     (a + combining diaeresis) where the artifact stored composed ä; those
 *     hash differently and were causing pages to fall through to live
 *     translation (the stray "translate bubble", e.g. QualityLand's ä/ö/ü/ß).
 *   • strip soft-hyphens + zero-width characters that hyphenation/rendering
 *     can inject on one side but not the other.
 *  Applied to BOTH the stored source and the query, so it can't desync. */
export function normalizeForHash(s: string): string {
  return (s || "")
    .normalize("NFC")
    .replace(/[­​‌‍﻿]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function txCacheKey(bookId: string, text: string, lang: string): string {
  return `pv.txh.${bookId}.${textHash(normalizeForHash(text))}.${lang}`;
}

// Bump when the translator prompt/model changes enough that old cached
// translations should be considered stale — baked into the Dexie key so
// old rows simply stop matching (and eventually age out via pruning).
const TX_CACHE_VERSION = "v1";
// Per-book row cap for the Dexie cache. Rows are ≤ ~7 KB of translated
// text, so 800 rows ≈ a few MB per book worst-case — roomy but bounded.
const TX_CACHE_MAX_ROWS_PER_BOOK = 800;

function txDexieKey(bookId: string, text: string, lang: string): string {
  return `${bookId}|${textHash(normalizeForHash(text))}|${lang}|${TX_CACHE_VERSION}`;
}

/** Fire-and-forget write to the durable Dexie layer, with LRU pruning
 *  per book once the row count passes the cap. */
function persistTranslationToDexie(
  bookId: string,
  sourceText: string,
  lang: string,
  translated: string,
): void {
  void (async () => {
    try {
      await db.translations.put({
        key: txDexieKey(bookId, sourceText, lang),
        bookId,
        lang,
        translated,
        updatedAt: Date.now(),
      });
      const count = await db.translations.where("bookId").equals(bookId).count();
      if (count > TX_CACHE_MAX_ROWS_PER_BOOK) {
        const rows = await db.translations
          .where("bookId")
          .equals(bookId)
          .sortBy("updatedAt");
        const excess = rows.slice(0, count - TX_CACHE_MAX_ROWS_PER_BOOK);
        await db.translations.bulkDelete(excess.map((r) => r.key));
      }
    } catch {
      /* Dexie unavailable (private mode, quota) — L1 still works. */
    }
  })();
}

/** Async cache read: sync L1 first, then the durable Dexie L2. A Dexie
 *  hit is backfilled into localStorage so subsequent SYNC reads (the
 *  translation read-aloud loop polls synchronously) also hit, and its
 *  updatedAt is touched so LRU pruning keeps revisited pages. */
export async function readTranslationCacheAsync(
  bookId: string,
  cfi: string | undefined,
  lang: string,
  text?: string,
): Promise<string | null> {
  const sync = readTranslationCache(bookId, cfi, lang, text);
  if (sync) return sync;
  if (!text) return null;
  try {
    const key = txDexieKey(bookId, text, lang);
    const row = await db.translations.get(key);
    if (row?.translated) {
      try {
        localStorage.setItem(txCacheKey(bookId, text, lang), row.translated);
      } catch {
        /* L1 quota — Dexie hit still returned */
      }
      void db.translations
        .update(key, { updatedAt: Date.now() })
        .catch(() => { /* touch is best-effort */ });
      return row.translated;
    }
  } catch {
    /* Dexie unavailable — treat as miss */
  }
  return null;
}

// Legacy CFI-based cache key — kept ONLY so we can read existing
// translations cached under the old scheme during the v0.5 → v0.6
// transition. New writes use text-hash cache keys via txCacheKey.
function legacyCfiCacheKey(bookId: string, cfi: string | undefined, lang: string): string {
  return `pv.tx.${bookId}.${cfi || "_"}.${lang}`;
}

/** Look up a translation by the TEXT of the page rather than by CFI.
 *  Text-hash matching is what makes the look-ahead actually count:
 *  pages pre-translated under synthetic CFIs (because they were in a
 *  later spine section) now produce a cache hit when the user lands on
 *  them, regardless of which CFI the reader assigns. 2026-06-29 fix:
 *  the previous CFI-based cache wasted ~80% of pre-translations
 *  because synthetic look-ahead CFIs never matched the real-arrival
 *  visible-page CFIs. */
export function readTranslationCache(
  bookId: string,
  cfi: string | undefined,
  lang: string,
  text?: string,
): string | null {
  try {
    // Prefer text-hash if we have the text
    if (text) {
      const hit = localStorage.getItem(txCacheKey(bookId, text, lang));
      if (hit) return hit;
    }
    // Fall back to the legacy CFI key — only useful for caches saved
    // before this build went live.
    if (cfi) {
      return localStorage.getItem(legacyCfiCacheKey(bookId, cfi, lang));
    }
    return null;
  } catch {
    return null;
  }
}

export function writeTranslationCache(
  bookId: string,
  cfi: string | undefined,
  lang: string,
  text: string,
  translated?: string,
): void {
  // Backwards-compat: older callers passed `text` as the actual
  // translated string and didn't pass `translated`. We can't tell
  // those apart from a new-style call where `text` is the SOURCE and
  // `translated` is the OUTPUT. So we keep the legacy interface and
  // detect: if `translated` is supplied, that's the new style.
  const sourceText = translated !== undefined ? text : "";
  const translatedText = translated !== undefined ? translated : text;
  // Durable L2 write — needs the source text for the hash key, so the
  // legacy (translated-only) call shape can't participate.
  if (sourceText) {
    persistTranslationToDexie(bookId, sourceText, lang, translatedText);
  }
  try {
    if (sourceText) {
      localStorage.setItem(txCacheKey(bookId, sourceText, lang), translatedText);
    }
    // Also write under the CFI key so legacy lookups (and intra-session
    // CFI-keyed reads in flight) still resolve.
    if (cfi) {
      localStorage.setItem(legacyCfiCacheKey(bookId, cfi, lang), translatedText);
    }
  } catch {
    /* localStorage quota hit — drop the cache write silently. */
  }
}

// ————— Chunked translation (2026-07-11) —————
// A full 3 KB page through Gemma takes ~70s+; the old single-call
// design meant nothing painted until the WHOLE page came back (and any
// client timeout killed everything). We now split pages into
// paragraph-level chunks, translate them sequentially (llama-server is
// single-stream, so parallel calls would just queue server-side and
// delay first paint), render each as it lands, and treat a failed
// chunk as retryable rather than fatal.

/** Per-chunk fetch timeout — generous safety net. Chunks are paragraph
 *  sized, so typical latency is seconds; nginx /lib allows 180s. */
const TRANSLATE_CHUNK_TIMEOUT_MS = 150_000;
/** Merge short paragraphs up to this many chars per request. */
const CHUNK_TARGET = 700;
/** Split paragraphs longer than this at sentence boundaries. */
const CHUNK_MAX = 1200;

function splitSentences(text: string, target: number): string[] {
  if (!text) return [];
  const parts = text.split(/(?<=[.!?…»”"])\s+/);
  const out: string[] = [];
  let cur = "";
  for (const p of parts) {
    if (cur && cur.length + p.length + 1 > target) {
      out.push(cur);
      cur = p;
    } else {
      cur = cur ? cur + " " + p : p;
    }
  }
  if (cur) out.push(cur);
  return out;
}

/** A translation chunk with its source-paragraph span, so translated
 *  output can be reassembled into REAL paragraphs. Without this, a long
 *  Cervantes paragraph split across chunks rendered as several stubby
 *  pseudo-paragraphs — "single lines, hard to read" (Thomas,
 *  2026-07-11). */
export interface TranslationChunk {
  text: string;
  /** Index of the source paragraph this chunk starts in. */
  paraStart: number;
  /** Index of the source paragraph this chunk ends in. */
  paraEnd: number;
}

/** Split page text into translation chunks. Prefers real paragraph
 *  boundaries when the caller has them; merges short paragraphs (drama
 *  dialogue would otherwise become dozens of requests — joined with
 *  \n\n so Gemma can preserve the breaks) and splits oversized ones at
 *  sentence boundaries (tracking that the pieces share one paragraph).
 *  Total capped at 7000 chars to match the legacy full-page limit. */
export function splitTranslationChunks(
  text: string,
  paras?: string[],
): TranslationChunk[] {
  const clean = (s: string) => (s || "").replace(/\s+/g, " ").trim();
  const units: string[] = (paras && paras.length > 0
    ? paras.map(clean)
    : // No paragraph info — treat the page as one flowing paragraph
      // split at sentence boundaries (matches the old flat rendering).
      [clean(text)]
  ).filter((p) => p.length > 0);

  const pieces: TranslationChunk[] = [];
  units.forEach((u, paraIdx) => {
    if (u.length > CHUNK_MAX) {
      for (const s of splitSentences(u, CHUNK_TARGET)) {
        pieces.push({ text: s, paraStart: paraIdx, paraEnd: paraIdx });
      }
    } else {
      pieces.push({ text: u, paraStart: paraIdx, paraEnd: paraIdx });
    }
  });

  // Merge small neighbors up to CHUNK_TARGET. Same-paragraph pieces
  // rejoin with a space; distinct paragraphs keep a \n\n marker that
  // survives translation and drives the rebuild step.
  const merged: TranslationChunk[] = [];
  for (const c of pieces) {
    const last = merged.length > 0 ? merged[merged.length - 1] : undefined;
    if (last && last.text.length + c.text.length + 2 <= CHUNK_TARGET) {
      const sep = last.paraEnd === c.paraStart ? " " : "\n\n";
      last.text = last.text + sep + c.text;
      last.paraEnd = c.paraEnd;
    } else {
      merged.push({ ...c });
    }
  }
  // Cap cumulative size at the legacy 7000-char page limit.
  const out: TranslationChunk[] = [];
  let total = 0;
  for (const m of merged) {
    if (total + m.text.length > 7000) break;
    out.push(m);
    total += m.text.length;
  }
  return out.length > 0 ? out : merged.slice(0, 1);
}

/** Rebuild real paragraphs from per-chunk translations. Chunks that
 *  continue the same source paragraph are glued with a space; \n
 *  breaks the model preserved inside a chunk (merged short paragraphs)
 *  become separate paragraphs again. Pending (null) chunks are skipped
 *  so progressive rendering shows only landed text. */
export function assembleTranslatedParagraphs(
  chunks: TranslationChunk[],
  results: (string | null | undefined)[],
): string[] {
  const out: string[] = [];
  let prev: TranslationChunk | null = null;
  let prevLanded = false;
  for (let i = 0; i < chunks.length; i++) {
    const r = results[i];
    if (!r) {
      prev = chunks[i];
      prevLanded = false;
      continue;
    }
    const parts = r
      .split(/\n+/)
      .map((s) => s.replace(/\s+/g, " ").trim())
      .filter(Boolean);
    if (
      prevLanded &&
      prev &&
      prev.paraEnd === chunks[i].paraStart &&
      out.length > 0 &&
      parts.length > 0
    ) {
      out[out.length - 1] = out[out.length - 1] + " " + parts[0];
      out.push(...parts.slice(1));
    } else {
      out.push(...parts);
    }
    prev = chunks[i];
    prevLanded = true;
  }
  return out;
}

/** How many chunks have landed as a contiguous run from the top of the
 *  page. Progressive rendering deliberately shows only this PREFIX: a
 *  hole in the middle (a chunk still out, or one that failed) would
 *  otherwise splice paragraph 5 directly above paragraph 3 and the
 *  reader would silently read the page out of order. */
export function landedPrefixCount(
  results: (string | null | undefined)[],
): number {
  let n = 0;
  while (n < results.length && results[n]) n += 1;
  return n;
}

/** Split the visible page into "translated so far" + "still original".
 *
 *  This is the core of the never-block-the-reader rule. A full page is
 *  ~50s through local Gemma; making the user watch a spinner for that
 *  long is the single worst thing the translate overlay does. So the
 *  page is rendered COMPLETE from the first frame: the chunks that have
 *  landed render as translated paragraphs, and everything the translator
 *  hasn't reached yet renders immediately as source text underneath.
 *  The page then upgrades to English paragraph by paragraph as chunks
 *  arrive, and the user can start reading the top within seconds (or
 *  keep reading the original, or just turn the page). */
export function progressiveSplit(
  chunks: TranslationChunk[],
  results: (string | null | undefined)[],
  sourceParas: string[],
): { done: string[]; pending: string[] } {
  const n = landedPrefixCount(results);
  const done = assembleTranslatedParagraphs(
    chunks.slice(0, n),
    results.slice(0, n),
  );
  if (n >= chunks.length) return { done, pending: [] };
  let firstPending = chunks[n].paraStart;
  // A long source paragraph can be split across several chunks. If the
  // last landed chunk already ended INSIDE the pending chunk's
  // paragraph, the top of that paragraph is already on screen in
  // English — replaying the whole original would duplicate it, so the
  // original tail starts at the next paragraph instead.
  if (n > 0 && chunks[n - 1].paraEnd === chunks[n].paraStart) {
    firstPending += 1;
  }
  return { done, pending: sourceParas.slice(firstPending) };
}

// Foreground-priority gate (2026-07-11). The look-ahead loop fires all
// its page-sized requests in parallel, which fills every llama slot and
// starves the VISIBLE page's chunks — the user stares at a blank overlay
// while pages they haven't reached yet translate. While the visible
// page has chunks in flight, background fetches wait.
let fgActive = 0;
let fgIdleResolvers: (() => void)[] = [];
function fgBegin(): void {
  fgActive += 1;
}
function fgEnd(): void {
  fgActive = Math.max(0, fgActive - 1);
  if (fgActive === 0) {
    const rs = fgIdleResolvers;
    fgIdleResolvers = [];
    rs.forEach((r) => r());
  }
}
function fgIdle(): Promise<void> {
  if (fgActive === 0) return Promise.resolve();
  return new Promise((r) => fgIdleResolvers.push(r));
}
/** Wait until NO foreground (visible-page) work is in flight, re-checking
 *  after each wake-up. A single `await fgIdle()` was racy: every waiter is
 *  released at once, and if the user flipped a page in that same tick the
 *  new visible page had already called fgBegin() again — the background
 *  waiter would still fire its request and land in the llama queue ahead
 *  of the page the user is actually staring at. At ~50s per page that
 *  mis-ordering is the difference between "instant" and "stuck". */
async function awaitForegroundIdle(): Promise<void> {
  while (fgActive > 0) {
    await fgIdle();
  }
}

/** One /lib/translate round-trip with its own ≥120s timeout, chained
 *  to the caller's abort signal. */
export async function postTranslateChunk(
  text: string,
  lang: string,
  outer?: AbortSignal,
): Promise<string> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), TRANSLATE_CHUNK_TIMEOUT_MS);
  const onOuter = () => ctrl.abort();
  outer?.addEventListener("abort", onOuter);
  try {
    const r = await fetch(remoteUrl("/lib/translate"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, target_lang: lang }),
      signal: ctrl.signal,
    });
    if (!r.ok) {
      const detail = await r.text();
      throw new Error(`${r.status} ${detail.slice(0, 120)}`);
    }
    const data = (await r.json()) as { translated?: string };
    const out = (data.translated || "").trim();
    if (!out) throw new Error("empty translation");
    return out;
  } finally {
    window.clearTimeout(timer);
    outer?.removeEventListener("abort", onOuter);
  }
}

/** Reusable translator — called by ReaderView's look-ahead loop (and
 *  kept as the programmatic API for full-page translation). Now chunks
 *  internally: cached chunks are instant, misses round-trip through
 *  /lib/translate one paragraph-group at a time, and the assembled
 *  page is written under the legacy full-page text-hash key so the
 *  visible-page overlay and read-aloud poller hit it exactly as
 *  before. */
export async function fetchTranslation(
  bookId: string,
  cfi: string | undefined,
  text: string,
  lang: string,
  signal?: AbortSignal,
  paras?: string[],
): Promise<string> {
  const trimmed = (text || "").replace(/\s+/g, " ").trim();
  if (!trimmed) return "";
  // Full-page cache key stays hashed on the truncated string — see
  // 2026-06-29 note: look-ahead and visible-page keys must collide.
  const body = trimmed.slice(0, 7000);
  const cached = await readTranslationCacheAsync(bookId, cfi, lang, body);
  if (cached) return cached;
  const chunks = splitTranslationChunks(text, paras);
  const outputs: (string | null)[] = [];
  for (const chunk of chunks) {
    if (signal?.aborted) throw new DOMException("aborted", "AbortError");
    const hit = await readTranslationCacheAsync(bookId, undefined, lang, chunk.text);
    if (hit) {
      outputs.push(hit);
      continue;
    }
    // Background politeness: yield to the visible page's chunk loop, and
    // keep yielding if the user turned the page while we waited. The
    // look-ahead exists to make page turns instant — it must never be the
    // reason the page in front of the user takes 50s.
    await awaitForegroundIdle();
    if (signal?.aborted) throw new DOMException("aborted", "AbortError");
    const out = await postTranslateChunk(chunk.text, lang, signal);
    writeTranslationCache(bookId, undefined, lang, chunk.text, out);
    outputs.push(out);
  }
  const joined = assembleTranslatedParagraphs(chunks, outputs)
    .join("\n\n")
    .trim();
  if (!joined) throw new Error("empty translation");
  writeTranslationCache(bookId, cfi, lang, body, joined);
  return joined;
}

/** A page that's ALREADY in the target language needs no translation. Foreign
 *  classics ship with English front/back matter (Project Gutenberg title +
 *  license pages); sending that to Gemma is pointless and, when it's busy,
 *  fails with "translation didn't load". Conservative on purpose: only fires
 *  when English stopwords clearly dominate over German/French/Spanish/Italian
 *  ones, so a real foreign body page is never misread as English. */
function isLikelyEnglish(text: string): boolean {
  const words = text.toLowerCase().match(/[a-z']+/g) || [];
  if (words.length < 8) return false;
  const EN = new Set([
    "the", "and", "of", "to", "is", "in", "this", "that", "you", "for", "with",
    "are", "or", "it", "as", "be", "by", "from", "which", "on", "at", "an",
    "was", "not", "have", "has", "your", "under", "terms", "may", "project",
    "gutenberg", "ebook", "license",
  ]);
  const FOREIGN = new Set([
    "und", "der", "die", "das", "ist", "nicht", "ein", "eine", "sich", "mit",
    "auch", "aber", "auf", "für", "den", "dem", "ich", "sie", "als", "wie",
    "les", "des", "une", "dans", "est", "pour", "avec", "que", "qui", "los",
    "las", "una", "por", "con", "para", "della", "che", "non", "gli", "nel",
    "dei", "il", "la", "le",
  ]);
  let en = 0, fn = 0;
  for (const w of words) {
    if (EN.has(w)) en++;
    if (FOREIGN.has(w)) fn++;
  }
  return en / words.length >= 0.12 && en > fn * 2;
}

function themePalette(theme: Props["theme"]) {
  switch (theme) {
    case "paper":
      return { bg: "#fdfcf8", fg: "#1b1712", muted: "#8a8577" };
    case "white":
      return { bg: "#ffffff", fg: "#1a1a1a", muted: "#6b6b6b" };
    case "sepia":
      return { bg: "#f4ecd8", fg: "#5b4636", muted: "#8a7a64" };
    case "gray":
      return { bg: "#cfd2cd", fg: "#1a1a1a", muted: "#4a4a4a" };
    case "dark":
    default:
      return { bg: "#0e0e10", fg: "#e8e6e1", muted: "#9b988f" };
  }
}

export function TranslatePanel({
  bookId,
  pageText,
  pageParas,
  pageCfi,
  fontFamily,
  fontSize,
  lineHeight,
  theme,
  lang,
  margin,
  bottomBarHeight,
  aheadStatus,
  onTranslated,
  speechCharIndex,
  navRef,
}: Props) {
  // ── TX COLUMNS (2026-10-09) ────────────────────────────────────────────
  // The overlay used to be a single `overflow-y: auto` box holding the whole
  // translated page as free-flowing paragraphs. Measured at his real phone
  // size (440x686, from his own stageD telemetry): the box is 559px tall,
  // the content ran 721-875px, and ALL 11 pages sampled overflowed by
  // 162-316px. So every translated page had to be scrolled, and a page whose
  // text happened to be short rendered as a couple of lines in a tall empty
  // box. That is "some pages only have one line. some scroll".
  //
  // The cause is not styling: English is simply longer than the German or
  // Spanish page it replaces, so one source page's translation does not fit
  // one page of space. It needs MORE pages, which the overlay had no way to
  // express.
  //
  // Fix: lay the translated text out in CSS columns exactly one view wide
  // and one view tall, and let a page turn consume a column before it
  // advances the book. The browser's own column engine does the page
  // breaking — the same mechanism epub.js uses for the original view — so
  // the two cannot drift, and the book's position is never touched: we add
  // pages WITHIN a page rather than repaginating the spine. NO_REPAGINATION
  // on the original view is untouched by construction.
  const colBoxRef = useRef<HTMLDivElement | null>(null);
  const [col, setCol] = useState(0);
  const [cols, setCols] = useState(1);
  const [colW, setColW] = useState(0);
  const colRef = useRef(0);
  const colsRef = useRef(1);
  colRef.current = col;
  colsRef.current = cols;

  const palette = themePalette(theme);
  const [busy, setBusy] = useState(false);
  const [translated, setTranslated] = useState<string>("");
  const [error, setError] = useState<string>("");
  // Chunked-translation state: source chunks for the current page, the
  // translated text per chunk (null = not yet landed), and the indices
  // that failed on the last pass (retryable via the pill).
  const [srcChunks, setSrcChunks] = useState<TranslationChunk[]>([]);
  const [chunkResults, setChunkResults] = useState<(string | null)[]>([]);
  const [failedChunks, setFailedChunks] = useState<number[]>([]);
  const [retryTick, setRetryTick] = useState(0);
  // User escape hatch. Translation is best-effort and slow (~50s/page on
  // local Gemma); the reader must ALWAYS be able to fall back to the
  // book's own words rather than wait on us. Per-page, not sticky —
  // turning translation off entirely is the ✕ in Settings.
  const [showOriginal, setShowOriginal] = useState(false);
  // Elapsed seconds while the visible-page request is in flight. Ticks
  // once a second so the status strip shows real motion instead of a
  // mute "Translating…" that looks hung — feedback 2026-06-28.
  const [elapsed, setElapsed] = useState<number>(0);
  const abortRef = useRef<AbortController | null>(null);
  const startedAtRef = useRef<number>(0);
  // Refs for values the translation loop READS but whose identity
  // must not restart it. onTranslated is an inline arrow in ReaderView
  // (new identity every parent render) — having it in the effect deps
  // aborted in-flight translation on every unrelated re-render, so a
  // slow page could churn forever without ever finishing. (2026-07-11)
  const onTranslatedRef = useRef(onTranslated);
  onTranslatedRef.current = onTranslated;
  const pageTextRef = useRef(pageText);
  pageTextRef.current = pageText;
  const pageParasRef = useRef(pageParas);
  pageParasRef.current = pageParas;

  const requestText = useMemo(() => {
    return (pageText || "").replace(/\s+/g, " ").trim().slice(0, 7000);
  }, [pageText]);

  /** The visible page in its ORIGINAL language, as paragraphs. This is
   *  what we can always show instantly — no model, no network — so it
   *  backs both the "show original" toggle and the not-yet-translated
   *  tail during progressive render. */
  const sourceParas = useMemo(() => {
    const clean = (s: string) => (s || "").replace(/\s+/g, " ").trim();
    if (pageParas && pageParas.length > 0) {
      return pageParas.map(clean).filter(Boolean);
    }
    return (pageText || "")
      .split(/\n{2,}/)
      .map(clean)
      .filter(Boolean);
  }, [pageParas, pageText]);

  // A page turn is a fresh decision — don't strand the reader in
  // original-text mode on a page that may well be cached and instant.
  useEffect(() => {
    setShowOriginal(false);
  }, [requestText]);

  useEffect(() => {
    if (!requestText) {
      setTranslated("");
      setError("");
      setBusy(false);
      setSrcChunks([]);
      setChunkResults([]);
      setFailedChunks([]);
      return;
    }
    // PRE-TRANSLATION FAST PATH. If the whole book is pre-translated (a
    // foreign classic with a shipped artifact, or an imported book we
    // translated in the background), map this page's paragraphs straight to
    // English — no Gemma, instant, and it writes the same page-cache the
    // read-aloud loop reads from, so audio never waits either. Only for an
    // English target; a partial cover returns null and we fall through.
    if (lang === "English" && hasPretranslation(bookId)) {
      // pageParas comes from the reader's visible-RANGE extraction, which can
      // come back empty on iOS — and an empty array makes pageEnglish bail
      // instantly, so a perfectly good 4626-pair map still live-translated every
      // page ("pre 4626" + "Translating 0/4"). Fall back to splitting the page
      // text itself so the matcher always has something to work with.
      // Split the page into paragraphs. The double-newline split alone was
      // the bug behind "every paragraph fills the whole page": epub.js often
      // returns page text with SINGLE newlines between paragraphs, so
      // /\n{2,}/ matched nothing, the whole page became one string, and the
      // renderer drew it as a single <p>. Fall back to single newlines when
      // the strict split yields one blob that's clearly more than a paragraph.
      const splitParas = (t: string | undefined | null): string[] => {
        if (!t) return [];   // never let an undefined page text throw
        const strict = t.split(/\n{2,}/).map((x) => x.trim()).filter(Boolean);
        if (strict.length > 1) return strict;
        const loose = t.split(/\n+/).map((x) => x.trim()).filter(Boolean);
        return loose.length > 1 ? loose : strict;
      };
      const paras =
        pageParasRef.current && pageParasRef.current.length > 0
          ? pageParasRef.current
          : splitParas(pageTextRef.current || requestText);
      const pre = pageEnglish(bookId, paras);
      if (pre) {
        setTranslated(pre);
        setError("");
        setBusy(false);
        setChunkResults([]);
        setFailedChunks([]);
        writeTranslationCache(bookId, pageCfi, lang, requestText, pre);
        onTranslatedRef.current?.(pageCfi, pre);
        return;
      }
    }
    // ALREADY-ENGLISH PAGE. A foreign classic's Project Gutenberg front/back
    // matter (title page, license) is English even in a German/French/etc book.
    // There's nothing to translate — show it as-is rather than sending English
    // to Gemma, which is pointless and errors ("translation didn't load") when
    // it's busy. This is what made the opening pages of classics stall.
    if (lang === "English" && isLikelyEnglish(requestText)) {
      setTranslated(requestText);
      setError("");
      setBusy(false);
      setChunkResults([]);
      setFailedChunks([]);
      writeTranslationCache(bookId, pageCfi, lang, requestText, requestText);
      onTranslatedRef.current?.(pageCfi, requestText);
      return;
    }
    // Full-page cache first (sync L1). Pass requestText so the cache
    // can match on text-hash, not just CFI — that's how look-ahead
    // pre-translations count.
    const cached = readTranslationCache(bookId, pageCfi, lang, requestText);
    if (cached) {
      setTranslated(cached);
      setError("");
      setBusy(false);
      setChunkResults([]);
      setFailedChunks([]);
      onTranslatedRef.current?.(pageCfi, cached);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setBusy(true);
    setError("");
    setTranslated("");
    setElapsed(0);
    startedAtRef.current = Date.now();
    // Claim foreground priority SYNCHRONOUSLY — before any await — so
    // the look-ahead loop (whose effect runs after this child effect)
    // can't slip its requests into the llama queue ahead of the
    // visible page. Released in the finally below; cached pages
    // release almost immediately.
    fgBegin();
    (async () => {
      try {
        // Durable full-page cache (Dexie) before any network.
        const stored = await readTranslationCacheAsync(
          bookId, pageCfi, lang, requestText,
        );
        if (ctrl.signal.aborted) return;
        if (stored) {
          setTranslated(stored);
          setChunkResults([]);
          setFailedChunks([]);
          onTranslatedRef.current?.(pageCfi, stored);
          return;
        }
        // Chunked path: paragraph-level requests, sequential (the LLM
        // is single-stream), first visible chunk first so the top of
        // the page paints in seconds instead of after the whole page.
        const chunks = splitTranslationChunks(
          pageTextRef.current, pageParasRef.current,
        );
        const results: (string | null)[] = new Array(chunks.length).fill(null);
        setSrcChunks(chunks);
        setChunkResults([...results]);
        setFailedChunks([]);
        const failed: number[] = [];
        for (let i = 0; i < chunks.length; i++) {
          if (ctrl.signal.aborted) return;
          try {
            const hit = await readTranslationCacheAsync(
              bookId, undefined, lang, chunks[i].text,
            );
            if (ctrl.signal.aborted) return;
            let out = hit;
            if (!out) {
              out = await postTranslateChunk(chunks[i].text, lang, ctrl.signal);
              writeTranslationCache(bookId, undefined, lang, chunks[i].text, out);
            }
            results[i] = out;
            setChunkResults([...results]);
          } catch (err) {
            if (ctrl.signal.aborted) return;
            console.warn("[pv-translate] chunk %d failed", i, err);
            failed.push(i);
            setFailedChunks([...failed]);
          }
        }
        if (ctrl.signal.aborted) return;
        if (failed.length === 0) {
          // Rebuild real paragraphs from the chunk outputs before the
          // final render + cache write.
          const joined = assembleTranslatedParagraphs(chunks, results)
            .join("\n\n")
            .trim();
          if (!joined) throw new Error("empty translation");
          setTranslated(joined);
          // Full-page cache write (text-hash + legacy CFI key) so the
          // read-aloud poller and future visits hit instantly.
          writeTranslationCache(bookId, pageCfi, lang, requestText, joined);
          onTranslatedRef.current?.(pageCfi, joined);
        } else if (failed.length === chunks.length) {
          setError("translate failed");
        }
      } catch (err) {
        if ((err as { name?: string })?.name === "AbortError") return;
        setError((err as Error).message || "translate failed");
      } finally {
        fgEnd();
        if (abortRef.current === ctrl) {
          setBusy(false);
        }
      }
    })();
    return () => {
      ctrl.abort();
    };
    // NOTE: pageText/pageParas/onTranslated are intentionally read via
    // refs — requestText already captures page-content changes, and
    // unstable identities must not abort in-flight translation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, pageCfi, lang, requestText, retryTick]);

  /** Retry pill handler — re-runs the whole effect; chunks that already
   *  succeeded resolve instantly from the chunk cache, so only the
   *  failed ones actually re-fetch. */
  const retryFailed = useCallback(() => {
    setRetryTick((t) => t + 1);
  }, []);

  // WATCHDOG (2026-07-20) — the "stuck at 0/2 with no counter" page.
  // Backend verified healthy; the failure mode is front-end state: the
  // panel sits with busy=false, nothing translated, no error, no failed
  // chunks — yet there IS page text. However the state got dropped (an
  // aborted run whose replacement never issued, a fast path that
  // early-returned on a stale snapshot), the recovery is identical:
  // re-kick the pipeline. Every terminal state of the main effect
  // (translated / error / failedChunks / busy) disarms this, so a
  // healthy page never sees an extra request — it can only fire while
  // the panel is visibly doing nothing with an untranslated page.
  useEffect(() => {
    if (busy || !requestText || showOriginal) return;
    if (translated || error || failedChunks.length > 0) return;
    const id = window.setTimeout(() => {
      console.warn(
        "[pv-translate] watchdog: idle with no translation — re-kicking",
      );
      setRetryTick((t) => t + 1);
    }, 4000);
    return () => window.clearTimeout(id);
  }, [busy, requestText, showOriginal, translated, error, failedChunks]);

  // ————— Progressive render model (2026-07-19) —————
  // A live page costs ~50s. Rather than gate the whole page on the last
  // chunk, we derive what to paint on EVERY chunk arrival: the landed
  // prefix in English, the rest still in the book's own language. The
  // reader is never looking at a spinner and never has to wait to keep
  // reading — the page just improves under them.
  const progressive = useMemo(() => {
    // Guard against stale chunk state: the pretranslation / cache fast
    // paths clear chunkResults without clearing srcChunks, and mixing a
    // previous page's chunks with this page's paragraphs would render
    // nonsense.
    if (srcChunks.length === 0 || chunkResults.length !== srcChunks.length) {
      return { done: [] as string[], pending: [] as string[] };
    }
    return progressiveSplit(srcChunks, chunkResults, sourceParas);
  }, [srcChunks, chunkResults, sourceParas]);

  /** Chunks landed / chunks total for the honest status readout. Chunks
   *  are paragraph-level groups, so "3/7 paragraphs" is accurate enough
   *  to be worth saying and infinitely better than an opaque pulse. */
  const landedCount = useMemo(
    () => chunkResults.filter(Boolean).length,
    [chunkResults],
  );
  const totalCount = chunkResults.length;

  /** True when the fully-assembled page is ready and the user hasn't
   *  asked for the original. Only in this state do we use the
   *  `paraSpans` path, which carries read-aloud word highlighting —
   *  the TTS loop speaks `translated`, so highlighting a progressive
   *  half-page would point at the wrong words. */
  const showFinal = !showOriginal && !!translated;

  /** Paragraphs to paint when we're NOT on the final path: either the
   *  user asked for the original, or translation is still in progress.
   *  `original: true` paragraphs render muted while translating so the
   *  boundary between "done" and "still coming" is obvious. */
  const bodyParas = useMemo((): { text: string; original: boolean }[] => {
    if (showOriginal) {
      return sourceParas.map((t) => ({ text: t, original: true }));
    }
    if (translated) return [];
    if (progressive.done.length > 0 || progressive.pending.length > 0) {
      return [
        ...progressive.done.map((t) => ({ text: t, original: false })),
        ...progressive.pending.map((t) => ({ text: t, original: true })),
      ];
    }
    // Nothing has landed yet (cache probe / first chunk in flight).
    // Show the original rather than a dead spinner.
    return sourceParas.map((t) => ({ text: t, original: true }));
  }, [showOriginal, translated, progressive, sourceParas]);

  // ————— Read-aloud word highlighting (2026-07-11) —————
  // The read-aloud loop speaks the cached full-page translation — the
  // exact string in `translated` — and reports a character index via
  // onBoundary. Map that index to (paragraph, word) here and highlight
  // it, mirroring the word highlight the normal reader shows in the
  // iframe.
  const paraSpans = useMemo(() => {
    const spans: { text: string; start: number }[] = [];
    if (!translated) return spans;
    let idx = 0;
    for (const part of translated.split(/(\n+)/)) {
      if (part && !/^\n+$/.test(part) && part.trim()) {
        spans.push({ text: part, start: idx });
      }
      idx += part.length;
    }
    return spans;
  }, [translated]);

  const activeParaIdx = useMemo(() => {
    if (speechCharIndex == null || speechCharIndex < 0) return -1;
    return paraSpans.findIndex(
      (p) =>
        speechCharIndex >= p.start &&
        speechCharIndex < p.start + p.text.length + 2,
    );
  }, [speechCharIndex, paraSpans]);

  const activeParaRef = useRef<HTMLParagraphElement | null>(null);
  // Scroll-friendly follow (2026-07-11): the translated page is often
  // TALLER than the original, so the overlay scrolls — and readers use
  // that to read ahead while the voice speaks. The follow logic must
  // not fight them: only scroll when the spoken paragraph is actually
  // out of view, scroll the overlay box directly (scrollIntoView also
  // scrolls ancestors), and back off for 4s after any manual scroll.
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  const lastUserScrollRef = useRef(0);
  const programmaticScrollUntilRef = useRef(0);
  const onScrollBox = useCallback(() => {
    if (performance.now() < programmaticScrollUntilRef.current) return;
    lastUserScrollRef.current = performance.now();
  }, []);
  useEffect(() => {
    if (activeParaIdx < 0) return;
    const box = scrollBoxRef.current;
    const el = activeParaRef.current;
    if (!box || !el) return;
    // Respect manual read-ahead — no yanking for 4s after user scroll.
    if (performance.now() - lastUserScrollRef.current < 4000) return;
    const elTop = el.offsetTop;
    const elBottom = elTop + el.offsetHeight;
    const viewTop = box.scrollTop;
    const viewBottom = viewTop + box.clientHeight;
    if (elTop >= viewTop && elBottom <= viewBottom) return; // already visible
    try {
      programmaticScrollUntilRef.current = performance.now() + 700;
      box.scrollTo({
        top: Math.max(0, elTop - box.clientHeight * 0.25),
        behavior: "smooth",
      });
    } catch {
      box.scrollTop = Math.max(0, elTop - box.clientHeight * 0.25);
    }
  }, [activeParaIdx]);

  const renderSpokenPara = useCallback(
    (para: { text: string; start: number }, charIdx: number): ReactNode[] => {
      const nodes: ReactNode[] = [];
      const re = /\S+/g;
      let m: RegExpExecArray | null;
      let last = 0;
      let key = 0;
      while ((m = re.exec(para.text)) !== null) {
        const s = m.index;
        const e = s + m[0].length;
        if (s > last) nodes.push(para.text.slice(last, s));
        const active =
          charIdx >= para.start + s && charIdx < para.start + e;
        nodes.push(
          <span
            key={key++}
            style={
              active
                ? {
                    backgroundColor: "rgba(240, 214, 139, 0.4)",
                    borderRadius: 3,
                    boxShadow: "0 0 0 2px rgba(240, 214, 139, 0.4)",
                  }
                : undefined
            }
          >
            {m[0]}
          </span>,
        );
        last = e;
      }
      if (last < para.text.length) nodes.push(para.text.slice(last));
      return nodes;
    },
    [],
  );

  // Tick the elapsed counter while busy so the status strip shows
  // "Translating… 12s" rather than just "Translating…" — the static
  // string reads as hung after a few seconds even when Gemma is fine.
  useEffect(() => {
    if (!busy) return;
    const id = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [busy]);

  // Re-measure the column box whenever its size or its content changes.
  // `scrollWidth` is how wide the laid-out columns are in total, so the
  // page count falls straight out of it — no text measuring of our own.
  useEffect(() => {
    const box = colBoxRef.current;
    if (!box) return;
    const measure = () => {
      const w = box.clientWidth;
      if (w <= 0) return; // zero-size stage: never divide by it
      const gap = margin * 2;
      const n = Math.max(1, Math.round((box.scrollWidth + gap) / (w + gap)));
      setColW(w);
      setCols(n);
      setCol((c) => Math.min(c, n - 1));
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(box);
    const id = window.setTimeout(measure, 120); // after fonts settle
    return () => {
      ro.disconnect();
      window.clearTimeout(id);
    };
  }, [margin, fontSize, lineHeight, fontFamily, translated, bodyParas, showOriginal]);

  // A new page means a new column run. Reset to the first column so a
  // forward turn lands at the top of the new page, not mid-way through it.
  useEffect(() => {
    setCol(0);
  }, [pageCfi]);

  // Publish the handle ReaderView's page turn consults.
  useEffect(() => {
    if (!navRef) return;
    navRef.current = {
      hasNext: () => colRef.current < colsRef.current - 1,
      hasPrev: () => colRef.current > 0,
      next: () => setCol((c) => Math.min(c + 1, colsRef.current - 1)),
      prev: () => setCol((c) => Math.max(0, c - 1)),
      toEnd: () => setCol(Math.max(0, colsRef.current - 1)),
    };
    return () => {
      navRef.current = null;
    };
  }, [navRef]);

  return (
    <div
      role="region"
      aria-label={`Translation in ${lang}`}
      className="absolute inset-x-0 top-0 z-30 flex flex-col overflow-hidden"
      style={{
        backgroundColor: palette.bg,
        color: palette.fg,
        // Cover from the very top of the reader area down to JUST above
        // the bottom button bar — that bar stays visible and the page-
        // flip tap zones on the sides continue to work because they're
        // positioned-fixed in their own layer.
        bottom: bottomBarHeight,
        paddingTop: "env(safe-area-inset-top, 0px)",
      }}
    >
      <div
        ref={scrollBoxRef}
        onScroll={onScrollBox}
        className="relative flex-1 overflow-hidden"
        style={{
          // Match the reader iframe's margin so the translated column
          // sits exactly where the original column did. No top crown
          // padding any more — text starts at the top of the reading
          // area, same as a real page (design brief 2026-06-27).
          padding: `${margin}px ${margin}px`,
          paddingTop: `calc(env(safe-area-inset-top, 0px) + ${margin}px)`,
        }}
      >
        {/* TX COLUMNS: a clipped viewport (above) over a column track
            (below). The track is exactly one viewport wide per column and
            full height, so the browser breaks the translated text into
            page-sized columns itself. Shifting by whole columns is a page
            turn; nothing scrolls. */}
        <div
          ref={colBoxRef}
          style={{
            height: "100%",
            columnWidth: colW > 0 ? `${colW}px` : undefined,
            columnGap: `${margin * 2}px`,
            columnFill: "auto",
            transform: `translateX(-${col * (colW + margin * 2)}px)`,
            transition: "transform 120ms ease-out",
            willChange: "transform",
          }}
        >
        {/* PROGRESSIVE BODY (2026-07-19). Replaces the old 128px amber
            pulse, which was a dead spinner for the ~50s a live page
            takes through Gemma — the user could neither read nor tell
            what was happening. Now the page always has words on it:
            translated paragraphs at the top as chunks land, the rest of
            the page still in the original language (muted) below, and
            the whole thing swapped to the original outright when the
            user taps "Show original". Honest progress lives in the
            status strip at the bottom. */}
        {!showFinal && bodyParas.length > 0 && (
          <div
            style={{
              fontFamily,
              fontSize: `${fontSize}px`,
              lineHeight,
              color: palette.fg,
            }}
            aria-busy={busy || undefined}
          >
            {bodyParas.map((p, i) => (
              <p
                key={i}
                style={{
                  margin: "0 0 1em",
                  // Untranslated text is muted ONLY while we're still
                  // working on it — when the reader has deliberately
                  // asked for the original it's the primary content and
                  // gets full contrast.
                  color:
                    p.original && !showOriginal ? palette.muted : palette.fg,
                }}
              >
                {p.text}
              </p>
            ))}
          </div>
        )}

        {/* Retry pill — chunk-level failures are non-fatal; already-
            translated paragraphs stay on screen and this re-runs only
            the misses (successes resolve from the chunk cache). */}
        {!busy && failedChunks.length > 0 && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={retryFailed}
              className="rounded-full px-4 py-2 text-sm"
              style={{
                backgroundColor: "rgba(240, 214, 139, 0.15)",
                border: "1px solid rgba(240, 214, 139, 0.5)",
                color: palette.fg,
                fontFamily: "system-ui, sans-serif",
              }}
            >
              {failedChunks.length === chunkResults.length
                ? "Translation didn't load — tap to retry"
                : `${failedChunks.length} paragraph${
                    failedChunks.length > 1 ? "s" : ""
                  } didn't translate — tap to retry`}
            </button>
          </div>
        )}

        {!busy && error && failedChunks.length === 0 && (
          <div
            className="mx-auto mt-8 max-w-xl rounded border px-4 py-3"
            style={{
              borderColor: "rgba(248, 113, 113, 0.4)",
              color: "rgb(248, 113, 113)",
              backgroundColor: "rgba(248, 113, 113, 0.08)",
              fontFamily: "system-ui, sans-serif",
            }}
          >
            <div className="text-base font-semibold">Translation error</div>
            <div className="mt-1 text-sm">{error}</div>
          </div>
        )}

        {showFinal && !error && (
          <div
            style={{
              fontFamily,
              fontSize: `${fontSize}px`,
              lineHeight,
              color: palette.fg,
            }}
          >
            {/* Paragraph-per-<p> rendering. Cached values written by
                the chunked pipeline carry \n\n between rebuilt
                paragraphs; legacy flat cache entries render as a
                single paragraph, same as before. The paragraph being
                read aloud gets word-level highlighting + auto-scroll. */}
            {paraSpans.map((p, i) => (
              <p
                key={i}
                ref={i === activeParaIdx ? activeParaRef : undefined}
                style={{ margin: "0 0 1em" }}
              >
                {i === activeParaIdx && speechCharIndex != null
                  ? renderSpokenPara(p, speechCharIndex)
                  : p.text}
              </p>
            ))}
          </div>
        )}

        {!busy && !error && !translated && !requestText && bodyParas.length === 0 && (
          <div
            className="mx-auto mt-12 max-w-xl text-center"
            style={{ color: palette.muted, fontFamily: "system-ui, sans-serif" }}
          >
            No visible page text to translate.
          </div>
        )}
        </div>
      </div>

      {/* Translation status strip — pinned at the bottom of the overlay
          (just above the reserved button-bar space). Priority: error
          first, then the current-page busy state, then the look-ahead
          status from ReaderView. Always rendered when the overlay is
          on so the user has continuous feedback. */}
      <div
        className="flex items-center gap-2 px-3 py-2 text-xs"
        style={{
          color: palette.muted,
          fontFamily: "system-ui, sans-serif",
          borderTop: `1px solid ${palette.muted}22`,
          backgroundColor: `${palette.bg}`,
          minHeight: 28,
        }}
      >
        {/* HONEST STATUS (2026-07-19). The old strip said "Translating
            page… 41s", which tells the reader nothing except that they
            are waiting. Chunks are paragraph-level, so we can report
            real units of work landed — "translating 2/6 paragraphs" —
            and the reader can see it move. Look-ahead status from
            ReaderView is appended so they also know how many pages are
            already banked ahead of them. */}
        <span className="min-w-0 flex-1 truncate italic" aria-live="polite">
          {error && failedChunks.length === 0
            ? `Error: ${error}`
            : busy
              ? `${
                  totalCount > 0
                    ? `Translating ${landedCount}/${totalCount} paragraph${
                        totalCount > 1 ? "s" : ""
                      }`
                    : "Preparing translation"
                } · ${elapsed}s${aheadStatus ? ` · ${aheadStatus}` : ""}`
              : failedChunks.length > 0
                ? `${failedChunks.length} paragraph${
                    failedChunks.length > 1 ? "s" : ""
                  } didn't translate`
                : aheadStatus || "Ready"}
        </span>

        {/* PRETRANSLATION BINDING BADGE (2026-07-19). Whether the whole-book
            English map bound is the single most important thing to know when a
            book is translating slowly — and the console log is unreadable on a
            phone. Show it: "pre 4626" means the map is serving pages instantly;
            "live" means we never bound and every page costs ~50s. Tapping is not
            needed — it's a readout, deliberately terse so it fits the strip. */}
        {(() => {
          // The loaded map is the source of truth — bindStatus can be empty
          // when a cached map short-circuited the bind path.
          const size = pretranslationSize(bookId);
          const raw = getBindStatus(bookId);
          const b = size > 0
            ? { ...raw, bound: true, pairs: raw.pairs ?? size }
            : raw;
          const title = b.bound
            ? `pretranslation bound via ${b.via}${b.id ? ` → ${b.id}` : ""}` +
              `${b.pairs ? `, ${b.pairs} pairs` : ""}` +
              `${b.sampleRate !== undefined ? `, sample ${(b.sampleRate * 100).toFixed(0)}%` : ""}`
            : `no pretranslation bound — live translating. tried: ${
                (b.tried || []).join(", ") || "nothing"
              }`;
          const miss = getLastMiss(bookId);
          return (
            <span
              title={miss ? `${title}\nlast unmatched paragraph: ${miss}` : title}
              className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums"
              style={{
                background: b.bound ? "rgba(52,211,153,.12)" : "rgba(245,158,11,.14)",
                color: b.bound ? "#34d399" : "#f59e0b",
              }}
            >
              {b.bound ? `pre ${b.pairs ?? ""}${miss ? "·m" : ""}`.trim() : "live"}
            </span>
          );
        })()}

        {/* Retry stays reachable from the strip too — the in-body pill
            scrolls away on a long page. */}
        {!busy && failedChunks.length > 0 && (
          <button
            type="button"
            onClick={retryFailed}
            className="shrink-0 rounded-full px-3 py-1"
            style={{
              backgroundColor: "rgba(240, 214, 139, 0.15)",
              border: "1px solid rgba(240, 214, 139, 0.5)",
              color: palette.fg,
            }}
          >
            Retry
          </button>
        )}

        {/* NEVER BLOCK THE READER. One tap drops back to the book's own
            words, mid-translation or not. Translation keeps running in
            the background, so toggling back picks up whatever landed
            meanwhile. */}
        {sourceParas.length > 0 && (
          <button
            type="button"
            onClick={() => setShowOriginal((v) => !v)}
            aria-pressed={showOriginal}
            className="shrink-0 rounded-full px-3 py-1"
            style={{
              border: `1px solid ${palette.muted}55`,
              color: palette.fg,
              backgroundColor: showOriginal
                ? "rgba(240, 214, 139, 0.15)"
                : "transparent",
            }}
          >
            {showOriginal ? "Show translation" : "Show original"}
          </button>
        )}
      </div>
    </div>
  );
}
